import {
	PROPOSAL_DOCUMENT_TYPE,
	buildProposalPayload,
	createProposalTemplateContent,
	deriveProposalFilename,
	generateProposalHtml,
	normalizeProposalContent,
} from '@/utils/proposalDocument';
import {
	QUOTATION_DOCUMENT_TYPE,
	buildQuotationPayload,
	createQuotationTemplateContent,
	deriveQuotationFilename,
	generateQuotationHtml,
	normalizeQuotationContent,
} from '@/utils/quotationDocument';
import {
	ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
	ASSIGNMENT_CONFIRMATION_SOURCE_TEMPLATE,
	ASSIGNMENT_CONFIRMATION_TEMPLATE,
	buildAssignmentConfirmationPayload,
	createAssignmentConfirmationTemplateContent,
	deriveAssignmentConfirmationFilename,
	generateAssignmentConfirmationHtml,
	normalizeAssignmentConfirmationContent,
} from '@/utils/assignmentConfirmationDocument';

const joinUniqueSiteAddresses = (values = []) =>
	[...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))].join(' · ');

export const CLIENT_DOCUMENT_DEFINITIONS = {
	[PROPOSAL_DOCUMENT_TYPE]: {
		type: PROPOSAL_DOCUMENT_TYPE,
		label: 'Proposal',
		lowerLabel: 'proposal',
		template: 'cleaning-proposal-v1',
		sourceTemplate: 'CP_202605-01_ST&WED - Strand & Wedgewood Apartments Harrow.pdf',
		createContent: createProposalTemplateContent,
		normalizeContent: normalizeProposalContent,
		buildPayload: buildProposalPayload,
		generateHtml: generateProposalHtml,
		deriveFilename: deriveProposalFilename,
		getReference: (content = {}) => content.reference || '',
		getDate: (content = {}) => content.proposalDate || null,
		getCounterparty: (content = {}) => content.clientName || '',
		getSiteAddress: (content = {}) =>
			joinUniqueSiteAddresses(
				(Array.isArray(content.properties) ? content.properties : []).map(
					(property) => property?.address,
				),
			),
	},
	[QUOTATION_DOCUMENT_TYPE]: {
		type: QUOTATION_DOCUMENT_TYPE,
		label: 'Quotation',
		lowerLabel: 'quotation',
		template: 'quotation-v1',
		sourceTemplate: 'FLR_Spectron_After_Builders_Clean_Quotation.docx',
		createContent: createQuotationTemplateContent,
		normalizeContent: normalizeQuotationContent,
		buildPayload: buildQuotationPayload,
		generateHtml: generateQuotationHtml,
		deriveFilename: deriveQuotationFilename,
		getReference: (content = {}) => content.quotationNumber || content.reference || '',
		getDate: (content = {}) => content.quotationDate || null,
		getCounterparty: (content = {}) => content.clientName || '',
		getSiteAddress: (content = {}) => content.serviceDetails?.siteAddress || '',
	},
	[ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE]: {
		type: ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
		label: 'Assignment confirmation',
		lowerLabel: 'assignment confirmation',
		template: ASSIGNMENT_CONFIRMATION_TEMPLATE,
		sourceTemplate: ASSIGNMENT_CONFIRMATION_SOURCE_TEMPLATE,
		createContent: createAssignmentConfirmationTemplateContent,
		normalizeContent: normalizeAssignmentConfirmationContent,
		buildPayload: buildAssignmentConfirmationPayload,
		generateHtml: generateAssignmentConfirmationHtml,
		deriveFilename: deriveAssignmentConfirmationFilename,
		getReference: (content = {}) => content.reference || '',
		getDate: (content = {}) => content.commenceDate || null,
		getCounterparty: (content = {}) => content.subcontractorName || '',
		getSiteAddress: (content = {}) => content.propertyAddress || '',
	},
};

export const normalizeClientDocumentType = (value) => {
	const type = String(value || '').toLowerCase();
	return CLIENT_DOCUMENT_DEFINITIONS[type]?.type || PROPOSAL_DOCUMENT_TYPE;
};

export const resolveClientDocumentType = (document = {}) =>
	normalizeClientDocumentType(document.documentType || document.content?.documentType);

export const getClientDocumentDefinition = (value) =>
	CLIENT_DOCUMENT_DEFINITIONS[normalizeClientDocumentType(value)] ||
	CLIENT_DOCUMENT_DEFINITIONS[PROPOSAL_DOCUMENT_TYPE];

export const createClientDocumentContent = (documentType) => {
	const definition = getClientDocumentDefinition(documentType);
	return definition.createContent();
};

export const normalizeClientDocumentContent = (content = {}, documentType = null) => {
	const type = normalizeClientDocumentType(documentType || content?.documentType);
	return getClientDocumentDefinition(type).normalizeContent(content);
};

export const buildClientDocumentPayload = (content = {}, extra = {}) => {
	const type = normalizeClientDocumentType(content?.documentType || extra?.documentType);
	return getClientDocumentDefinition(type).buildPayload(content, extra);
};

export const generateClientDocumentHtml = ({ document = null, content = null, logoUrl = '/logo-m.svg' } = {}) => {
	const source = content || document?.content || {};
	const type = resolveClientDocumentType({ ...(document || {}), content: source });
	const definition = getClientDocumentDefinition(type);
	return definition.generateHtml({ content: source, logoUrl });
};

export const deriveClientDocumentFilename = (document = {}) => {
	const definition = getClientDocumentDefinition(resolveClientDocumentType(document));
	return definition.deriveFilename(document);
};

export const getClientDocumentReference = (document = {}) => {
	const type = resolveClientDocumentType(document);
	const content = normalizeClientDocumentContent(document.content || document, type);
	return document.reference || getClientDocumentDefinition(type).getReference(content);
};

export const getClientDocumentDate = (document = {}) => {
	const type = resolveClientDocumentType(document);
	const content = normalizeClientDocumentContent(document.content || document, type);
	return document.documentDate || getClientDocumentDefinition(type).getDate(content);
};

export const getClientDocumentCounterparty = (document = {}) => {
	const type = resolveClientDocumentType(document);
	const content = normalizeClientDocumentContent(document.content || document, type);
	return document.clientName || document.client?.name || getClientDocumentDefinition(type).getCounterparty(content);
};

export const getClientDocumentSiteAddress = (document = {}) => {
	const explicitAddress = String(document.siteAddress || '').trim();
	if (explicitAddress) return explicitAddress;
	const type = resolveClientDocumentType(document);
	const content =
		document.content && typeof document.content === 'object' && !Array.isArray(document.content)
			? document.content
			: document;
	const derivedAddress = String(
		getClientDocumentDefinition(type).getSiteAddress?.(content) || '',
	).trim();
	return (
		derivedAddress ||
		String(document.metadata?.siteAddress || '').trim() ||
		String(document.propertySummary || '').trim()
	);
};

export const getClientDocumentLabel = (value) => {
	const type = typeof value === 'string' ? value : resolveClientDocumentType(value || {});
	return getClientDocumentDefinition(type).label;
};

export const getClientDocumentLowerLabel = (value) => {
	const type = typeof value === 'string' ? value : resolveClientDocumentType(value || {});
	return getClientDocumentDefinition(type).lowerLabel;
};
